// src/components/ComunicacaoModal.jsx
// ============================================================================
// Modal de comunicação com motoristas — BLB System
//
// MODOS:
//   mode="chat"      → conversa individual com trava de atendimento
//   mode="broadcast" → comunicado em massa
//
// REGRAS ATENDIDAS:
//   1. Mensagem enviada ao WhatsApp com nome do operador em negrito no backend
//   2. Realtime via Socket.io com token
//   3. Proteção visual contra conversa em uso por outro usuário
//   4. Botão de encerrar conversa
//   5. Layout refinado, responsivo e protegido contra nomes longos
// ============================================================================

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { io } from 'socket.io-client';
import {
    FaTimes,
    FaWhatsapp,
    FaSms,
    FaPaperPlane,
    FaBullhorn,
    FaCheck,
    FaCheckDouble,
    FaSpinner,
    FaExclamationTriangle,
    FaPhoneSlash,
    FaLock,
    FaStopCircle,
    FaUserShield,
    FaCircle
} from 'react-icons/fa';
import api from '../services/api';
import './ComunicacaoModal.css';

// ── Helpers ──────────────────────────────────────────────────────────────────

const getSocketToken = () => {
    return (
        localStorage.getItem('token') ||
        localStorage.getItem('authToken') ||
        localStorage.getItem('jwt') ||
        localStorage.getItem('accessToken') ||
        sessionStorage.getItem('token') ||
        sessionStorage.getItem('authToken') ||
        sessionStorage.getItem('jwt') ||
        sessionStorage.getItem('accessToken') ||
        null
    );
};

const normalizeId = (value) => {
    if (value === null || value === undefined) return null;
    return String(value);
};

const formatTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
};

const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';

    const hoje = new Date();
    const ontem = new Date(hoje);
    ontem.setDate(hoje.getDate() - 1);

    if (d.toDateString() === hoje.toDateString()) return 'Hoje';
    if (d.toDateString() === ontem.toDateString()) return 'Ontem';

    return d.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    });
};

const getInitials = (name = '') => {
    const clean = String(name || '').trim();
    if (!clean) return '?';

    const parts = clean.split(/\s+/).filter(Boolean);
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();

    return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
};

const StatusIcon = ({ status }) => {
    if (status === 'pendente') {
        return <FaSpinner className="com-status-icon spinning" title="Enviando..." />;
    }

    if (status === 'enviada') {
        return <FaCheck className="com-status-icon sent" title="Enviada" />;
    }

    if (status === 'entregue') {
        return <FaCheckDouble className="com-status-icon delivered" title="Entregue" />;
    }

    if (status === 'lida') {
        return <FaCheckDouble className="com-status-icon read" title="Lida" />;
    }

    if (status === 'falha') {
        return <FaExclamationTriangle className="com-status-icon failed" title="Falha" />;
    }

    return null;
};

const CanalBadge = ({ canal }) => {
    if (canal === 'sms') {
        return (
            <span className="com-canal-badge sms">
                <FaSms />
                SMS
            </span>
        );
    }

    return (
        <span className="com-canal-badge whatsapp">
            <FaWhatsapp />
            WhatsApp
        </span>
    );
};

// ── Componente principal ─────────────────────────────────────────────────────

export default function ComunicacaoModal({
    motoristaId = null,
    motoristaNome = 'Motorista',
    motoristaTel = '',
    motoristas = null,
    onClose
}) {
    const isBroadcast = Array.isArray(motoristas);
    const mode = isBroadcast ? 'broadcast' : 'chat';

    const [canal, setCanal] = useState('whatsapp');

    const [conversa, setConversa] = useState(null);
    const [mensagens, setMensagens] = useState([]);
    const [isLoadingChat, setIsLoadingChat] = useState(false);
    const [textoEnvio, setTextoEnvio] = useState('');
    const [isEnviando, setIsEnviando] = useState(false);

    const [lockInfo, setLockInfo] = useState(null);
    const [isEncerrando, setIsEncerrando] = useState(false);
    const [chatError, setChatError] = useState('');

    const [broadcastMsg, setBroadcastMsg] = useState('');
    const [broadcastStatus, setBroadcastStatus] = useState(null);
    const [isBroadcasting, setIsBroadcasting] = useState(false);

    const bottomRef = useRef(null);
    const textareaRef = useRef(null);
    const socketRef = useRef(null);

    const modalTitle = mode === 'broadcast' ? 'Comunicado em massa' : motoristaNome;
    const hasPhone = Boolean(String(motoristaTel || '').trim());
    const canSendChat = mode === 'chat' && hasPhone && !lockInfo && !isLoadingChat && !isEncerrando;

    // ── Carrega conversa ao abrir/trocar canal ───────────────────────────────
    useEffect(() => {
        if (mode !== 'chat') return;
        carregarConversa();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canal, motoristaId]);

    const carregarConversa = async () => {
        setIsLoadingChat(true);
        setMensagens([]);
        setConversa(null);
        setLockInfo(null);
        setChatError('');

        try {
            const { data } = await api.get(
                `/api/comunicacao/conversa/${motoristaId}?canal=${canal}`
            );

            if (data.conversa) {
                setConversa(data.conversa);

                const { data: hist } = await api.get(
                    `/api/comunicacao/mensagens/${data.conversa.id}`
                );

                setMensagens(hist.mensagens || []);
            } else {
                setConversa(null);
                setMensagens([]);
            }
        } catch (err) {
            const status = err?.response?.status;
            const payload = err?.response?.data;

            if (status === 423 || status === 409) {
                setLockInfo({
                    atendente_id: payload?.atendente_id,
                    atendente_nome: payload?.atendente_nome || 'outro usuário',
                    message: payload?.error || 'Esta conversa está em atendimento por outro usuário.'
                });
                return;
            }

            setChatError(payload?.error || 'Não foi possível carregar a conversa.');
        } finally {
            setIsLoadingChat(false);
        }
    };

    // ── Socket.io com token e sala tenant ────────────────────────────────────
    useEffect(() => {
        if (mode !== 'chat' || !conversa?.id || lockInfo) return;

        const token = getSocketToken();

        const socket = io(process.env.REACT_APP_SOCKET_URL || window.location.origin, {
            withCredentials: true,
            auth: { token },
            transports: ['websocket', 'polling']
        });

        socketRef.current = socket;

        socket.on('connect', () => {
            console.log('[comunicacao/socket] conectado:', socket.id);
        });

        socket.on('connect_error', (err) => {
            console.warn('[comunicacao/socket] erro:', err?.message);
        });

        const novaMensagemHandler = (data) => {
            if (normalizeId(data?.conversa_id) !== normalizeId(conversa.id)) return;

            setMensagens((prev) => {
                const exists = prev.some((m) => normalizeId(m.id) === normalizeId(data?.mensagem?.id));
                if (exists) return prev;
                return [...prev, data.mensagem];
            });
        };

        const statusHandler = (data) => {
            if (normalizeId(data?.conversa_id) !== normalizeId(conversa.id)) return;

            setMensagens((prev) =>
                prev.map((m) => {
                    const sameProviderId =
                        data?.zapi_message_id &&
                        m?.zapi_message_id &&
                        normalizeId(m.zapi_message_id) === normalizeId(data.zapi_message_id);

                    const sameLocalId =
                        data?.mensagem_id &&
                        normalizeId(m.id) === normalizeId(data.mensagem_id);

                    if (!sameProviderId && !sameLocalId) return m;

                    return {
                        ...m,
                        status: data.status || m.status
                    };
                })
            );
        };

        const conversaEncerradaHandler = (data) => {
            if (normalizeId(data?.conversa_id) !== normalizeId(conversa.id)) return;

            setConversa((prev) => {
                if (!prev) return prev;
                return {
                    ...prev,
                    status: 'encerrada',
                    encerrada_em: data.encerrada_em,
                    encerrada_por_nome: data.encerrada_por_nome
                };
            });

            setChatError(`Conversa encerrada por ${data.encerrada_por_nome || 'um usuário'}.`);
        };

        socket.on('comunicacao:nova_mensagem', novaMensagemHandler);
        socket.on('comunicacao:status_atualizado', statusHandler);
        socket.on('comunicacao:conversa_encerrada', conversaEncerradaHandler);

        return () => {
            socket.off('comunicacao:nova_mensagem', novaMensagemHandler);
            socket.off('comunicacao:status_atualizado', statusHandler);
            socket.off('comunicacao:conversa_encerrada', conversaEncerradaHandler);
            socket.disconnect();
            socketRef.current = null;
        };
    }, [mode, conversa?.id, lockInfo]);

    // ── Auto-scroll ──────────────────────────────────────────────────────────
    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [mensagens]);

    // ── Agrupa mensagens por data ────────────────────────────────────────────
    const mensagensAgrupadas = useMemo(() => {
        const grupos = [];
        let ultimaData = null;

        mensagens.forEach((m) => {
            const data = formatDate(m.criada_em);

            if (data !== ultimaData) {
                grupos.push({ tipo: 'separador', data });
                ultimaData = data;
            }

            grupos.push({ tipo: 'mensagem', ...m });
        });

        return grupos;
    }, [mensagens]);

    // ── Envio de mensagem individual ─────────────────────────────────────────
    const handleEnviar = async (e) => {
        e?.preventDefault();

        const msg = textoEnvio.trim();
        if (!msg || isEnviando || !canSendChat) return;

        setIsEnviando(true);
        setChatError('');

        const tempId = `temp-${Date.now()}`;

        const tempMsg = {
            id: tempId,
            conversa_id: conversa?.id || null,
            direcao: 'enviada',
            canal,
            conteudo: msg,
            enviado_por_nome: 'Você',
            status: 'pendente',
            criada_em: new Date().toISOString()
        };

        setMensagens((prev) => [...prev, tempMsg]);
        setTextoEnvio('');

        try {
            const { data } = await api.post('/api/comunicacao/enviar', {
                motoristaId,
                motoristaNome,
                telefone: motoristaTel,
                mensagem: msg,
                canal
            });

            setMensagens((prev) =>
                prev.map((m) =>
                    normalizeId(m.id) === normalizeId(tempId)
                        ? {
                              ...tempMsg,
                              id: data.mensagem_id,
                              conversa_id: data.conversa_id,
                              status: data.status,
                              zapi_message_id: data.zapi_message_id || null,
                              criada_em: data.criada_em || tempMsg.criada_em
                          }
                        : m
                )
            );

            if (!conversa) {
                setConversa({
                    id: data.conversa_id,
                    status: 'ativa'
                });
            }
        } catch (err) {
            const status = err?.response?.status;
            const payload = err?.response?.data;

            if (status === 423 || status === 409) {
                setLockInfo({
                    atendente_id: payload?.atendente_id,
                    atendente_nome: payload?.atendente_nome || 'outro usuário',
                    message: payload?.error || 'Esta conversa está em atendimento por outro usuário.'
                });
            } else {
                setChatError(payload?.error || 'Erro ao enviar mensagem.');
            }

            setMensagens((prev) =>
                prev.map((m) =>
                    normalizeId(m.id) === normalizeId(tempId)
                        ? { ...m, status: 'falha' }
                        : m
                )
            );
        } finally {
            setIsEnviando(false);
            textareaRef.current?.focus();
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleEnviar();
        }
    };

    // ── Encerrar conversa ────────────────────────────────────────────────────
    const handleEncerrarConversa = async () => {
        if (!conversa?.id || isEncerrando) return;

        setIsEncerrando(true);
        setChatError('');

        try {
            await api.patch(`/api/comunicacao/conversa/${conversa.id}/encerrar`);

            setConversa((prev) =>
                prev
                    ? {
                          ...prev,
                          status: 'encerrada',
                          encerrada_em: new Date().toISOString()
                      }
                    : prev
            );

            onClose?.();
        } catch (err) {
            setChatError(err?.response?.data?.error || 'Não foi possível encerrar a conversa.');
        } finally {
            setIsEncerrando(false);
        }
    };

    // ── Broadcast ────────────────────────────────────────────────────────────
    const handleBroadcast = async (e) => {
        e?.preventDefault();

        const msg = broadcastMsg.trim();
        if (!msg || isBroadcasting) return;

        setIsBroadcasting(true);
        setBroadcastStatus(null);

        try {
            const { data } = await api.post('/api/comunicacao/broadcast', {
                motoristas,
                mensagem: msg,
                canal
            });

            setBroadcastStatus(data);
        } catch (err) {
            alert(err.response?.data?.error || 'Erro ao enviar broadcast.');
        } finally {
            setIsBroadcasting(false);
        }
    };

    // ── Render ───────────────────────────────────────────────────────────────
    return (
        <div className="com-overlay" onClick={onClose}>
            <div
                className={`com-box ${mode === 'broadcast' ? 'com-box--broadcast' : ''}`}
                onClick={(e) => e.stopPropagation()}
            >
                {/* HEADER */}
                <header className="com-header">
                    <div className="com-header-main">
                        <div className={`com-avatar ${mode === 'broadcast' ? 'com-avatar--broadcast' : ''}`}>
                            {mode === 'broadcast' ? <FaBullhorn /> : getInitials(motoristaNome)}
                        </div>

                        <div className="com-header-text">
                            <div className="com-title-row">
                                <p className="com-header-name" title={modalTitle}>
                                    {modalTitle}
                                </p>

                                {mode === 'chat' && conversa?.status === 'ativa' && !lockInfo && (
                                    <span className="com-live-pill">
                                        <FaCircle />
                                        Ativa
                                    </span>
                                )}

                                {lockInfo && (
                                    <span className="com-lock-pill">
                                        <FaLock />
                                        Em uso
                                    </span>
                                )}
                            </div>

                            <p className="com-header-phone" title={mode === 'broadcast' ? '' : motoristaTel}>
                                {mode === 'broadcast'
                                    ? `${motoristas.length} motorista${motoristas.length !== 1 ? 's' : ''} selecionado${motoristas.length !== 1 ? 's' : ''}`
                                    : motoristaTel || 'Sem telefone cadastrado'}
                            </p>
                        </div>
                    </div>

                    <div className="com-header-actions">
                        <div className="com-canal-toggle" role="tablist" aria-label="Selecionar canal">
                            <button
                                type="button"
                                className={`com-canal-btn ${canal === 'whatsapp' ? 'active whatsapp' : ''}`}
                                onClick={() => setCanal('whatsapp')}
                                title="WhatsApp"
                            >
                                <FaWhatsapp />
                                <span>WhatsApp</span>
                            </button>

                            <button
                                type="button"
                                className={`com-canal-btn ${canal === 'sms' ? 'active sms' : ''}`}
                                onClick={() => setCanal('sms')}
                                title="SMS"
                            >
                                <FaSms />
                                <span>SMS</span>
                            </button>
                        </div>

                        {mode === 'chat' && conversa?.id && !lockInfo && (
                            <button
                                type="button"
                                className="com-end-btn"
                                onClick={handleEncerrarConversa}
                                disabled={isEncerrando}
                                title="Encerrar conversa"
                            >
                                {isEncerrando ? <FaSpinner className="spinning" /> : <FaStopCircle />}
                                <span>Encerrar</span>
                            </button>
                        )}

                        <button className="com-close-btn" onClick={onClose} title="Fechar">
                            <FaTimes />
                        </button>
                    </div>
                </header>

                {/* MODO CHAT */}
                {mode === 'chat' && (
                    <>
                        <div className={`com-chat-body canal-${canal}`}>
                            {isLoadingChat ? (
                                <div className="com-state">
                                    <FaSpinner className="spinning" />
                                    <span>Carregando histórico...</span>
                                </div>
                            ) : lockInfo ? (
                                <div className="com-state com-state-lock">
                                    <div className="com-state-icon lock">
                                        <FaLock />
                                    </div>
                                    <h3>Conversa em atendimento</h3>
                                    <p>
                                        Esta conversa já está sendo conduzida por{' '}
                                        <strong>{lockInfo.atendente_nome}</strong>.
                                    </p>
                                    <span>
                                        Para evitar mensagens duplicadas ou conflito de atendimento,
                                        aguarde a conversa ser encerrada.
                                    </span>
                                </div>
                            ) : chatError ? (
                                <div className="com-state com-state-error">
                                    <div className="com-state-icon danger">
                                        <FaExclamationTriangle />
                                    </div>
                                    <h3>Atenção</h3>
                                    <p>{chatError}</p>
                                </div>
                            ) : mensagensAgrupadas.length === 0 ? (
                                <div className="com-state">
                                    <div className={`com-empty-icon ${canal}`}>
                                        {canal === 'whatsapp' ? <FaWhatsapp /> : <FaSms />}
                                    </div>
                                    <p>Nenhuma mensagem ainda.</p>
                                    <span>Inicie a conversa pelo campo abaixo.</span>
                                </div>
                            ) : (
                                mensagensAgrupadas.map((item, i) => {
                                    if (item.tipo === 'separador') {
                                        return (
                                            <div key={`sep-${i}`} className="com-date-sep">
                                                <span>{item.data}</span>
                                            </div>
                                        );
                                    }

                                    const isEnviada = item.direcao === 'enviada';

                                    return (
                                        <div
                                            key={item.id}
                                            className={`com-bubble-wrap ${isEnviada ? 'sent' : 'received'}`}
                                        >
                                            <div className={`com-bubble ${isEnviada ? 'sent' : 'received'}`}>
                                                {!isEnviada && item.enviado_por_nome && (
                                                    <span className="com-bubble-author">
                                                        {item.enviado_por_nome}
                                                    </span>
                                                )}

                                                <p className="com-bubble-text">{item.conteudo}</p>

                                                <div className="com-bubble-meta">
                                                    <span className="com-bubble-time">
                                                        {formatTime(item.criada_em)}
                                                    </span>

                                                    {isEnviada && <StatusIcon status={item.status} />}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                            )}

                            <div ref={bottomRef} />
                        </div>

                        <footer className="com-footer">
                            {!hasPhone ? (
                                <div className="com-no-phone">
                                    <FaPhoneSlash />
                                    <span>Este motorista não tem telefone cadastrado.</span>
                                </div>
                            ) : lockInfo ? (
                                <div className="com-footer-lock">
                                    <FaUserShield />
                                    <span>
                                        Atendimento bloqueado para evitar conflito entre usuários.
                                    </span>
                                </div>
                            ) : (
                                <form className="com-input-row" onSubmit={handleEnviar}>
                                    <div className="com-input-shell">
                                        <textarea
                                            ref={textareaRef}
                                            className="com-textarea"
                                            placeholder={`Mensagem via ${canal === 'whatsapp' ? 'WhatsApp' : 'SMS'}...`}
                                            value={textoEnvio}
                                            onChange={(e) => setTextoEnvio(e.target.value)}
                                            onKeyDown={handleKeyDown}
                                            rows={1}
                                            disabled={isEnviando || isEncerrando}
                                            maxLength={canal === 'sms' ? 160 : 4096}
                                        />

                                        {canal === 'sms' && textoEnvio.length > 0 && (
                                            <span className="com-char-count">
                                                {textoEnvio.length}/160
                                            </span>
                                        )}
                                    </div>

                                    <button
                                        type="submit"
                                        className={`com-send-btn canal-${canal}`}
                                        disabled={!textoEnvio.trim() || isEnviando || isEncerrando}
                                        title="Enviar"
                                    >
                                        {isEnviando ? <FaSpinner className="spinning" /> : <FaPaperPlane />}
                                    </button>
                                </form>
                            )}
                        </footer>
                    </>
                )}

                {/* MODO BROADCAST */}
                {mode === 'broadcast' && (
                    <div className="com-broadcast-body">
                        <div className="com-broadcast-topline">
                            <CanalBadge canal={canal} />
                            <span>
                                O comunicado será enviado para os destinatários selecionados.
                            </span>
                        </div>

                        <div className="com-recipients">
                            <p className="com-recipients-label">Destinatários</p>

                            <div className="com-recipients-chips">
                                {motoristas.map((m) => (
                                    <span
                                        key={m.id}
                                        className={`com-chip ${!m.telefone ? 'no-phone' : ''}`}
                                        title={!m.telefone ? 'Sem telefone cadastrado' : m.telefone}
                                    >
                                        {!m.telefone && <FaPhoneSlash className="com-chip-icon" />}
                                        {m.nome}
                                    </span>
                                ))}
                            </div>

                            {motoristas.some((m) => !m.telefone) && (
                                <p className="com-recipients-warn">
                                    <FaExclamationTriangle />
                                    Motoristas sem telefone serão ignorados no envio.
                                </p>
                            )}
                        </div>

                        {!broadcastStatus && (
                            <form className="com-broadcast-form" onSubmit={handleBroadcast}>
                                <label className="com-broadcast-label">
                                    Mensagem do comunicado
                                    {canal === 'sms' && (
                                        <span className="com-sms-hint"> máximo de 160 caracteres</span>
                                    )}
                                </label>

                                <textarea
                                    className="com-broadcast-textarea"
                                    placeholder="Ex.: Reunião obrigatória às 14h na sede. Confirme presença respondendo esta mensagem."
                                    value={broadcastMsg}
                                    onChange={(e) => setBroadcastMsg(e.target.value)}
                                    rows={5}
                                    disabled={isBroadcasting}
                                    maxLength={canal === 'sms' ? 160 : 4096}
                                    required
                                />

                                {canal === 'sms' && (
                                    <span className="com-broadcast-counter">
                                        {broadcastMsg.length}/160
                                    </span>
                                )}

                                <button
                                    type="submit"
                                    className={`com-broadcast-send canal-${canal}`}
                                    disabled={!broadcastMsg.trim() || isBroadcasting}
                                >
                                    {isBroadcasting ? (
                                        <>
                                            <FaSpinner className="spinning" />
                                            Enviando...
                                        </>
                                    ) : (
                                        <>
                                            <FaBullhorn />
                                            Enviar comunicado
                                        </>
                                    )}
                                </button>
                            </form>
                        )}

                        {broadcastStatus && (
                            <div className="com-broadcast-report">
                                <div className="com-report-summary">
                                    <div className="com-report-stat success">
                                        <strong>{broadcastStatus.enviados}</strong>
                                        <span>Enviados</span>
                                    </div>

                                    <div className="com-report-stat danger">
                                        <strong>{broadcastStatus.falhas}</strong>
                                        <span>Falhas</span>
                                    </div>

                                    <div className="com-report-stat neutral">
                                        <strong>{broadcastStatus.ignorados}</strong>
                                        <span>Sem telefone</span>
                                    </div>
                                </div>

                                <div className="com-report-table-wrap">
                                    <table className="com-report-table">
                                        <thead>
                                            <tr>
                                                <th>Motorista</th>
                                                <th>Status</th>
                                            </tr>
                                        </thead>

                                        <tbody>
                                            {broadcastStatus.results.map((r) => (
                                                <tr key={r.id}>
                                                    <td>
                                                        <div className="com-report-driver">
                                                            <div className="com-avatar-sm">
                                                                {getInitials(r.nome)}
                                                            </div>
                                                            <span title={r.nome}>{r.nome}</span>
                                                        </div>
                                                    </td>

                                                    <td>
                                                        <span className={`com-report-badge status-${r.status}`}>
                                                            {r.status === 'enviada' && (
                                                                <>
                                                                    <FaCheck />
                                                                    Enviado
                                                                </>
                                                            )}

                                                            {r.status === 'falha' && (
                                                                <>
                                                                    <FaExclamationTriangle />
                                                                    Falha
                                                                </>
                                                            )}

                                                            {r.status === 'erro' && (
                                                                <>
                                                                    <FaExclamationTriangle />
                                                                    Erro
                                                                </>
                                                            )}

                                                            {r.status === 'sem_telefone' && (
                                                                <>
                                                                    <FaPhoneSlash />
                                                                    Sem telefone
                                                                </>
                                                            )}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                <button
                                    className="com-broadcast-retry"
                                    onClick={() => {
                                        setBroadcastStatus(null);
                                        setBroadcastMsg('');
                                    }}
                                >
                                    Enviar novo comunicado
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}